import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Music, Users } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/images";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

export default function PicnicElectronic() {
  const { t, lang } = useLanguage();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const formatDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(
    lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="electronic-page">
      <SEO title="Picnic Electronic — Sunset DJ Picnic Events, French Riviera | The Nice Picnic"
        description="Picnic Electronic: a premium collective picnic at sunset in secret spots on the Côte d'Azur. Organic & deep house DJ sets, Niçoise specialties, immersive atmosphere." />

      <section className="relative min-h-[70svh] flex items-end pt-20">
        <div className="absolute inset-0">
          <img src={IMAGES.electronicHero} alt="Picnic Electronic — sunset DJ set on the French Riviera" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181513]/80 via-[#181513]/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 w-full">
          <Reveal>
            <p className="overline-label !text-gold">{t("electronic.overline")}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ivory mt-4">Picnic Electronic</h1>
            <p className="mt-6 max-w-xl text-ivory/85 font-light leading-relaxed">{t("electronic.intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="electronic-events">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="overline-label">{t("electronic.upcoming")}</p>
            <div className="w-16 h-px bg-gold mt-6" />
          </Reveal>
          {events === null ? (
            <p className="mt-12 text-earth">{t("booking.loadingSlots")}</p>
          ) : events.length === 0 ? (
            <Reveal className="mt-12 border border-champagne rounded-3xl p-10 text-center">
              <p className="font-serif text-2xl text-ink italic">{t("electronic.empty")}</p>
            </Reveal>
          ) : (
            <div className="mt-12 space-y-8">
              {events.map((ev, i) => (
                <Reveal key={ev.id} delay={i * 0.08}>
                  <article data-testid={`event-card-${ev.id}`}
                    className="grid grid-cols-1 md:grid-cols-12 border border-champagne rounded-3xl overflow-hidden bg-cream/50">
                    <div className="md:col-span-4 h-56 md:h-auto">
                      <img src={ev.cover_image || IMAGES.electronicParty} alt={`${ev.title} — Picnic Electronic event`}
                        className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="md:col-span-8 p-7 sm:p-10">
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.18em] text-clay">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {formatDate(ev.date)}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {ev.start_time} – {ev.end_time}</span>
                      </div>
                      <h2 className="font-serif text-3xl text-ink mt-4">{ev.title}</h2>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-earth">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-olive" /> {ev.location}</span>
                        {ev.music && <span className="inline-flex items-center gap-1.5"><Music size={14} className="text-olive" /> {ev.music}</span>}
                        <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-olive" /> {ev.places_left} / {ev.capacity} {t("electronic.placesLeft")}</span>
                      </div>
                      <p className="mt-4 text-sm text-earth font-light leading-relaxed">
                        {lang === "en" && ev.description_en ? ev.description_en : ev.description_fr}
                      </p>
                      <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
                        <p className="font-serif text-3xl text-clay">{ev.price} € <span className="text-base text-earth">{t("common.perPerson")}</span></p>
                        {ev.places_left <= 0 ? (
                          <span data-testid={`event-soldout-${ev.id}`} className="text-xs uppercase tracking-[0.2em] text-burgundy border border-burgundy/30 rounded-full px-6 py-3">{t("electronic.soldOut")}</span>
                        ) : ev.booking_open ? (
                          <Link to={`/booking?experience=electronic&event=${ev.id}`} data-testid={`event-book-${ev.id}`} className="btn-primary">
                            {t("electronic.bookSpot")} <ArrowRight size={16} />
                          </Link>
                        ) : (
                          <span data-testid={`event-closed-${ev.id}`} className="text-xs uppercase tracking-[0.2em] text-earth border border-champagne rounded-full px-6 py-3">{t("electronic.bookingClosed")}</span>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
